/**
 * RoamerMarketplaceModule.ts — Module #10: the Roamer Marketplace™.
 *
 * A platform module (NOT a core engine): a curated marketplace of trusted
 * products/services for remote workers — NOT a general shopping platform.
 * Module pattern:
 *  • Registers with Roamer Core™ as an EngineAdapter via attachToCore().
 *  • Inbound ONLY via Core bus events + manual/curated entry:
 *    - LocationChanged ({ location: {...} } wrapped, flat fallback) →
 *      currentCity/currentCountry, driving location-aware discovery and
 *      arrival recommendations.
 *    - CommunityUpdated / PlacesUpdated are fusion metadata-only refresh
 *      signals — never data, never invented partners.
 *  • Outbound: the brief defines no domain events — the module publishes
 *    nothing. (The Core event union is fixed; nothing to add.)
 *  • Honesty: partners exist only as recorded/curated listings (future
 *    partner APIs sync real catalogs); community ratings derive from
 *    recorded Roamer Reports™ only (null when unrated); discovery filters
 *    exclude unknowns under demanded floors (unrated fails minRating,
 *    unpriced fails maxPrice, remoteWorkerFriendly null never passes);
 *    Match Score™ normalizes over evaluable factors only, null otherwise.
 *  • Only VERIFIED WorkRoamer members may submit Roamer Reports™ —
 *    enforced at add (and the load sanitizer drops unverified ones).
 *    Helpful votes are once-only per report (voted ids persisted).
 *  • Future commerce (affiliates with mandatory disclosure, booking refs,
 *    referrals, promo codes, partner API connectors) is reference records
 *    and registries only — NO payment processing, nothing is ever called.
 *  • Privacy: 5 toggles all default false. personalizedRecommendations
 *    gates match scores + smart recommendations (null/empty when off);
 *    export gates saved services, usage history, and preferences.
 *  • Persistence nomad:marketplace:v1 with record-by-record sanitization,
 *    caps, deep clones at read boundaries; export + deleteAll; injectable
 *    timestamps; handler errors never break the bus.
 */

import { roamerCore, type CoreEvent, type EngineAdapter } from '@/services/core';
import { storageManager } from '@/services/storage/StorageManager';
import {
  DEFAULT_MARKETPLACE_PREFERENCES,
  DEFAULT_MARKETPLACE_PRIVACY,
  MARKETPLACE_CATEGORIES,
  MARKETPLACE_REPORT_KINDS,
  type AffiliatePartnershipRef,
  type BookingRequestRef,
  type ContactMethod,
  type MarketplaceCategory,
  type MarketplaceExport,
  type MarketplaceFilters,
  type MarketplacePartner,
  type MarketplacePreferences,
  type MarketplacePrivacySettings,
  type MarketplaceReport,
  type MarketplaceReportKind,
  type PartnerApiConnector,
  type PartnerComparison,
  type PromoCodeRef,
  type ReferralRef,
  type RoamerMatchScore,
  type ServiceCollection,
  type SmartRecommendation,
  type UsageRecord,
} from './models';
import { communityRating, computeMatchScore, computeRecommendations } from './matching';

const STORAGE_KEY = 'nomad:marketplace:v1';
const MODULE_ID = 'roamer-marketplace-module';
const MAX_PARTNERS = 1000;
const MAX_REPORTS = 2000;
const MAX_USAGE = 1000;
const MAX_COLLECTIONS = 100;
const MAX_COMMERCE_REFS = 500;

const CONTACT_KINDS = ['email', 'phone', 'chat', 'form'] as const;
const BOOKING_STATUSES = ['draft', 'requested', 'confirmed', 'cancelled'] as const;

function asString(v: unknown): string | null {
  return typeof v === 'string' && v.trim().length > 0 ? v.trim() : null;
}
function asNumberOrNull(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
function asStringArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string') : [];
}
function clampRating(v: unknown): number | null {
  const n = asNumberOrNull(v);
  if (n == null) return null;
  return Math.max(1, Math.min(5, n));
}

interface PersistedState {
  privacy: MarketplacePrivacySettings;
  preferences: MarketplacePreferences;
  currentCity: string | null;
  currentCountry: string | null;
  partners: MarketplacePartner[];
  reports: MarketplaceReport[];
  votedReportIds: string[];
  usage: UsageRecord[];
  favorites: string[];
  collections: ServiceCollection[];
  affiliates: AffiliatePartnershipRef[];
  bookings: BookingRequestRef[];
  referrals: ReferralRef[];
  promoCodes: PromoCodeRef[];
  refreshSignalCount: number;
}

export type NewPartnerInput = Omit<MarketplacePartner, 'id' | 'addedAt'>;
export type NewReportInput = {
  partnerId: string;
  kind: MarketplaceReportKind;
  text: string;
  rating: number | null;
  /** Only verified WorkRoamer members may submit. */
  submittedByVerifiedMember: boolean;
};

export class RoamerMarketplaceModule {
  private privacy: MarketplacePrivacySettings = { ...DEFAULT_MARKETPLACE_PRIVACY };
  private preferences: MarketplacePreferences = structuredClone(DEFAULT_MARKETPLACE_PREFERENCES);
  private currentCity: string | null = null;
  private currentCountry: string | null = null;
  private partners: MarketplacePartner[] = [];
  private reports: MarketplaceReport[] = [];
  private votedReportIds: string[] = [];
  private usage: UsageRecord[] = [];
  private favorites: string[] = [];
  private collections: ServiceCollection[] = [];
  private affiliates: AffiliatePartnershipRef[] = [];
  private bookings: BookingRequestRef[] = [];
  private referrals: ReferralRef[] = [];
  private promoCodes: PromoCodeRef[] = [];
  private refreshSignalCount = 0;
  private apiConnectors: PartnerApiConnector[] = [];
  private unsubscribes: (() => void)[] = [];
  private core: typeof roamerCore | null = null;
  private listeners = new Set<() => void>();

  constructor(now: number = Date.now()) {
    this.load(now);
  }

  // ── Core integration ────────────────────────────────────────────────────────

  buildAdapter(): EngineAdapter {
    return {
      id: MODULE_ID,
      name: 'Roamer Marketplace™',
      version: '1.0.0',
      capabilities: ['partner-catalog', 'discovery', 'match-score', 'community-reviews', 'smart-recommendations', 'favorites'],
      healthCheck: async () => true,
    };
  }

  attachToCore(core: typeof roamerCore = roamerCore, now: number = Date.now()): void {
    this.detachFromCore();
    core.registerEngine(this.buildAdapter(), now);
    const safe = (handler: (e: CoreEvent) => void) => (e: CoreEvent) => {
      try {
        handler(e);
      } catch {
        /* a bad payload must never break the bus */
      }
    };
    this.unsubscribes.push(core.on('LocationChanged', safe((e) => this.onLocationChanged(e)), 'module:marketplace'));
    for (const name of ['CommunityUpdated', 'PlacesUpdated'] as const) {
      this.unsubscribes.push(core.on(name, safe(() => this.onRefreshSignal()), 'module:marketplace'));
    }
    this.core = core;
  }

  detachFromCore(): void {
    for (const u of this.unsubscribes) u();
    this.unsubscribes = [];
    this.core = null;
  }

  private onLocationChanged(e: CoreEvent): void {
    const raw = (e.payload ?? {}) as Record<string, unknown>;
    const p = (raw.location != null && typeof raw.location === 'object'
      ? raw.location
      : raw) as Record<string, unknown>;
    const city = asString(p.city);
    const country = asString(p.country);
    if (city === this.currentCity && country === this.currentCountry) return;
    if (city != null) this.currentCity = city;
    if (country != null) this.currentCountry = country;
    this.afterUpdate();
  }

  private onRefreshSignal(): void {
    this.refreshSignalCount += 1;
    this.afterUpdate();
  }

  getRefreshSignalCount(): number {
    return this.refreshSignalCount;
  }

  /** Read-only: whether the module is currently attached to Roamer Core. */
  isAttached(): boolean {
    return this.core != null;
  }

  getCurrentLocation(): { city: string | null; country: string | null } {
    return { city: this.currentCity, country: this.currentCountry };
  }

  // ── Partner catalog (curated, recorded-only) ────────────────────────────────

  addPartner(input: NewPartnerInput, at: number = Date.now()): MarketplacePartner | null {
    if (!asString(input.name) || !asString(input.description)) return null;
    if (!MARKETPLACE_CATEGORIES.includes(input.category)) return null;
    const partner: MarketplacePartner = {
      name: input.name.trim(),
      category: input.category,
      description: input.description.trim(),
      supportedCountries: asStringArray(input.supportedCountries),
      pricing: {
        amount: asNumberOrNull(input.pricing?.amount),
        currency: asString(input.pricing?.currency),
        period: input.pricing?.period === 'oneTime' || input.pricing?.period === 'monthly' || input.pricing?.period === 'yearly'
          ? input.pricing.period : null,
        summary: asString(input.pricing?.summary),
      },
      verified: input.verified === true,
      availableLanguages: asStringArray(input.availableLanguages),
      contactMethods: (input.contactMethods ?? []).filter(
        (c): c is ContactMethod => c != null && CONTACT_KINDS.includes(c.kind) && asString(c.value) != null,
      ),
      website: asString(input.website),
      remoteWorkerFriendly: typeof input.remoteWorkerFriendly === 'boolean' ? input.remoteWorkerFriendly : null,
      availabilityNote: asString(input.availabilityNote),
      id: `partner-${at}-${this.partners.length}`,
      addedAt: at,
    };
    this.partners.push(partner);
    if (this.partners.length > MAX_PARTNERS) this.partners = this.partners.slice(-MAX_PARTNERS);
    this.afterUpdate();
    return structuredClone(partner);
  }

  getPartner(id: string): MarketplacePartner | null {
    const partner = this.partners.find((p) => p.id === id);
    return partner ? structuredClone(partner) : null;
  }

  getCommunityRating(partnerId: string): number | null {
    return communityRating(this.reports, partnerId);
  }

  /** Discovery: unknowns never pass demanded floors. */
  discover(filters: MarketplaceFilters = {}): MarketplacePartner[] {
    const eqi = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();
    return structuredClone(this.partners.filter((p) => {
      if (filters.category != null && p.category !== filters.category) return false;
      if (filters.verifiedOnly && !p.verified) return false;
      if (filters.country != null && !p.supportedCountries.some((c) => eqi(c, filters.country!))) return false;
      if (filters.currentBaseOnly) {
        if (this.currentCountry == null) return false;
        if (!p.supportedCountries.some((c) => eqi(c, this.currentCountry!))) return false;
      }
      if (filters.minCommunityRating != null) {
        const rating = communityRating(this.reports, p.id);
        if (rating == null || rating < filters.minCommunityRating) return false;
      }
      if (filters.maxPrice != null) {
        if (p.pricing.amount == null || p.pricing.amount > filters.maxPrice) return false;
      }
      if (filters.language != null && !p.availableLanguages.some((l) => eqi(l, filters.language!))) return false;
      if (filters.availableOnly && p.availabilityNote == null) return false;
      if (filters.remoteWorkerFriendlyOnly && p.remoteWorkerFriendly !== true) return false;
      return true;
    }));
  }

  // ── Community reviews (Roamer Reports™) ─────────────────────────────────────

  /** Only verified WorkRoamer members may submit — hard rule. */
  addReport(input: NewReportInput, at: number = Date.now()): MarketplaceReport | null {
    if (input.submittedByVerifiedMember !== true) return null;
    if (!this.partners.some((p) => p.id === input.partnerId)) return null;
    if (!MARKETPLACE_REPORT_KINDS.includes(input.kind) || !asString(input.text)) return null;
    const report: MarketplaceReport = {
      id: `mreport-${at}-${this.reports.length}`,
      partnerId: input.partnerId,
      kind: input.kind,
      text: input.text.trim(),
      rating: clampRating(input.rating),
      submittedByVerifiedMember: true,
      helpfulVotes: 0,
      recordedAt: at,
    };
    this.reports.push(report);
    if (this.reports.length > MAX_REPORTS) this.reports = this.reports.slice(-MAX_REPORTS);
    this.afterUpdate();
    return structuredClone(report);
  }

  /** Helpful votes are once-only per report per device. */
  voteHelpful(reportId: string): boolean {
    const report = this.reports.find((r) => r.id === reportId);
    if (!report || this.votedReportIds.includes(reportId)) return false;
    report.helpfulVotes += 1;
    this.votedReportIds.push(reportId);
    this.afterUpdate();
    return true;
  }

  getReports(filter?: { partnerId?: string; kind?: MarketplaceReportKind }): MarketplaceReport[] {
    let list = this.reports;
    if (filter?.partnerId) list = list.filter((r) => r.partnerId === filter.partnerId);
    if (filter?.kind) list = list.filter((r) => r.kind === filter.kind);
    return structuredClone(list);
  }

  // ── Preferences, usage, Match Score™, recommendations ───────────────────────

  getPreferences(): MarketplacePreferences {
    return structuredClone(this.preferences);
  }

  updatePreferences(patch: Partial<MarketplacePreferences>): MarketplacePreferences {
    const safe: Partial<MarketplacePreferences> = {};
    if ('monthlyBudget' in patch) safe.monthlyBudget = asNumberOrNull(patch.monthlyBudget);
    if ('budgetCurrency' in patch) safe.budgetCurrency = asString(patch.budgetCurrency);
    if ('languages' in patch) safe.languages = asStringArray(patch.languages);
    if ('travelStyle' in patch) {
      safe.travelStyle = patch.travelStyle === 'budget' || patch.travelStyle === 'balanced' || patch.travelStyle === 'comfort'
        ? patch.travelStyle : null;
    }
    if ('currentNeeds' in patch) {
      safe.currentNeeds = (patch.currentNeeds ?? []).filter((c) => MARKETPLACE_CATEGORIES.includes(c));
    }
    this.preferences = { ...this.preferences, ...safe };
    this.afterUpdate();
    return this.getPreferences();
  }

  recordUsage(partnerId: string, outcome: UsageRecord['outcome'], note: string | null = null, at: number = Date.now()): UsageRecord | null {
    if (!this.partners.some((p) => p.id === partnerId)) return null;
    if (outcome !== 'positive' && outcome !== 'neutral' && outcome !== 'negative') return null;
    const record: UsageRecord = {
      id: `usage-${at}-${this.usage.length}`,
      partnerId,
      outcome,
      note: asString(note),
      recordedAt: at,
    };
    this.usage.push(record);
    if (this.usage.length > MAX_USAGE) this.usage = this.usage.slice(-MAX_USAGE);
    this.afterUpdate();
    return structuredClone(record);
  }

  getUsage(partnerId?: string): UsageRecord[] {
    const list = partnerId ? this.usage.filter((u) => u.partnerId === partnerId) : this.usage;
    return structuredClone(list);
  }

  /** Consent-gated personalization: null score when the toggle is off. */
  getMatchScore(partnerId: string): RoamerMatchScore | null {
    const partner = this.partners.find((p) => p.id === partnerId);
    if (!partner) return null;
    if (!this.privacy.personalizedRecommendations) {
      return { partnerId, score: null, breakdown: [], evaluatedFactors: 0 };
    }
    return structuredClone(computeMatchScore(partner, {
      currentCountry: this.currentCountry,
      preferences: this.preferences,
      reports: this.reports,
      usage: this.usage,
    }));
  }

  /** Consent-gated: empty when personalization is off. */
  getRecommendations(): SmartRecommendation[] {
    if (!this.privacy.personalizedRecommendations) return [];
    return structuredClone(computeRecommendations({
      currentCountry: this.currentCountry,
      preferences: this.preferences,
      reports: this.reports,
      usage: this.usage,
      partners: this.partners,
    }));
  }

  // ── Favorites, collections, comparison ──────────────────────────────────────

  toggleFavorite(partnerId: string): boolean {
    if (!this.partners.some((p) => p.id === partnerId)) return false;
    if (this.favorites.includes(partnerId)) {
      this.favorites = this.favorites.filter((id) => id !== partnerId);
    } else {
      this.favorites.push(partnerId);
    }
    this.afterUpdate();
    return true;
  }

  getFavorites(): MarketplacePartner[] {
    return structuredClone(this.partners.filter((p) => this.favorites.includes(p.id)));
  }

  createCollection(name: string, partnerIds: string[] = [], at: number = Date.now()): ServiceCollection | null {
    if (!asString(name)) return null;
    const collection: ServiceCollection = {
      id: `coll-${at}-${this.collections.length}`,
      name: name.trim(),
      partnerIds: partnerIds.filter((id) => this.partners.some((p) => p.id === id)),
      createdAt: at,
    };
    this.collections.push(collection);
    if (this.collections.length > MAX_COLLECTIONS) this.collections = this.collections.slice(-MAX_COLLECTIONS);
    this.afterUpdate();
    return structuredClone(collection);
  }

  addToCollection(collectionId: string, partnerId: string): boolean {
    const collection = this.collections.find((c) => c.id === collectionId);
    if (!collection || !this.partners.some((p) => p.id === partnerId)) return false;
    if (collection.partnerIds.includes(partnerId)) return false;
    collection.partnerIds.push(partnerId);
    this.afterUpdate();
    return true;
  }

  getCollections(): ServiceCollection[] {
    return structuredClone(this.collections);
  }

  /** Side-by-side comparison of recorded fields only. */
  compareProviders(partnerIds: string[]): PartnerComparison[] {
    return partnerIds
      .map((id) => this.partners.find((p) => p.id === id))
      .filter((p): p is MarketplacePartner => p != null)
      .map((p) => structuredClone({
        partnerId: p.id,
        name: p.name,
        category: p.category,
        pricingSummary: p.pricing.summary,
        verified: p.verified,
        communityRating: communityRating(this.reports, p.id),
        reviewCount: this.reports.filter((r) => r.partnerId === p.id).length,
        supportedCountries: p.supportedCountries,
        availableLanguages: p.availableLanguages,
      }));
  }

  /** Shareable recommendation — gated on partnerDataSharing consent. */
  shareRecommendation(partnerId: string): { partnerName: string; category: MarketplaceCategory; website: string | null } | null {
    if (!this.privacy.partnerDataSharing) return null;
    const partner = this.partners.find((p) => p.id === partnerId);
    if (!partner) return null;
    return { partnerName: partner.name, category: partner.category, website: partner.website };
  }

  // ── Future commerce (references + registries only — NO payments) ────────────

  addAffiliatePartnership(partnerId: string, program: string, disclosure: string, at: number = Date.now()): AffiliatePartnershipRef | null {
    if (!this.partners.some((p) => p.id === partnerId)) return null;
    // Transparency is mandatory: no affiliate record without a disclosure.
    if (!asString(program) || !asString(disclosure)) return null;
    const ref: AffiliatePartnershipRef = {
      id: `aff-${at}-${this.affiliates.length}`,
      partnerId,
      program: program.trim(),
      disclosure: disclosure.trim(),
    };
    this.affiliates.push(ref);
    if (this.affiliates.length > MAX_COMMERCE_REFS) this.affiliates = this.affiliates.slice(-MAX_COMMERCE_REFS);
    this.afterUpdate();
    return structuredClone(ref);
  }

  createBookingRequest(partnerId: string, note: string | null = null, at: number = Date.now()): BookingRequestRef | null {
    if (!this.partners.some((p) => p.id === partnerId)) return null;
    const ref: BookingRequestRef = {
      id: `book-${at}-${this.bookings.length}`,
      partnerId,
      status: 'draft',
      note: asString(note),
      createdAt: at,
    };
    this.bookings.push(ref);
    if (this.bookings.length > MAX_COMMERCE_REFS) this.bookings = this.bookings.slice(-MAX_COMMERCE_REFS);
    this.afterUpdate();
    return structuredClone(ref);
  }

  updateBookingStatus(id: string, status: BookingRequestRef['status']): boolean {
    const booking = this.bookings.find((b) => b.id === id);
    if (!booking || !BOOKING_STATUSES.includes(status)) return false;
    booking.status = status;
    this.afterUpdate();
    return true;
  }

  addReferral(partnerId: string, code: string, at: number = Date.now()): ReferralRef | null {
    if (!this.partners.some((p) => p.id === partnerId) || !asString(code)) return null;
    const ref: ReferralRef = { id: `ref-${at}-${this.referrals.length}`, partnerId, code: code.trim(), recordedAt: at };
    this.referrals.push(ref);
    if (this.referrals.length > MAX_COMMERCE_REFS) this.referrals = this.referrals.slice(-MAX_COMMERCE_REFS);
    this.afterUpdate();
    return structuredClone(ref);
  }

  addPromoCode(partnerId: string, code: string, description: string | null = null, expiresAt: number | null = null, at: number = Date.now()): PromoCodeRef | null {
    if (!this.partners.some((p) => p.id === partnerId) || !asString(code)) return null;
    const ref: PromoCodeRef = {
      id: `promo-${at}-${this.promoCodes.length}`,
      partnerId,
      code: code.trim(),
      description: asString(description),
      expiresAt: asNumberOrNull(expiresAt),
    };
    this.promoCodes.push(ref);
    if (this.promoCodes.length > MAX_COMMERCE_REFS) this.promoCodes = this.promoCodes.slice(-MAX_COMMERCE_REFS);
    this.afterUpdate();
    return structuredClone(ref);
  }

  getCommerceRefs(): { affiliates: AffiliatePartnershipRef[]; bookings: BookingRequestRef[]; referrals: ReferralRef[]; promoCodes: PromoCodeRef[] } {
    return structuredClone({
      affiliates: this.affiliates,
      bookings: this.bookings,
      referrals: this.referrals,
      promoCodes: this.promoCodes,
    });
  }

  /** FUTURE partner API integrations — registry only, nothing is ever called. */
  registerPartnerApiConnector(connector: PartnerApiConnector): void {
    if (!this.apiConnectors.some((c) => c.id === connector.id)) {
      this.apiConnectors.push(connector);
    }
  }

  getPartnerApiConnectors(): PartnerApiConnector[] {
    return [...this.apiConnectors];
  }

  // ── Privacy, export, delete ─────────────────────────────────────────────────

  getPrivacySettings(): MarketplacePrivacySettings {
    return { ...this.privacy };
  }

  updatePrivacySettings(patch: Partial<MarketplacePrivacySettings>): MarketplacePrivacySettings {
    this.privacy = { ...this.privacy, ...patch };
    this.afterUpdate();
    return this.getPrivacySettings();
  }

  exportMarketplace(now: number = Date.now()): MarketplaceExport {
    return structuredClone({
      exportedAt: now,
      partners: this.partners, // curated catalog — not personal data
      reports: this.reports,
      favorites: this.privacy.shareSavedServices ? this.favorites : [],
      collections: this.privacy.shareSavedServices ? this.collections : [],
      usage: this.privacy.sharePurchaseHistory ? this.usage : [],
      preferences: this.privacy.partnerDataSharing ? this.preferences : null,
    });
  }

  deleteAll(): void {
    this.privacy = { ...DEFAULT_MARKETPLACE_PRIVACY };
    this.preferences = structuredClone(DEFAULT_MARKETPLACE_PREFERENCES);
    this.currentCity = null;
    this.currentCountry = null;
    this.partners = [];
    this.reports = [];
    this.votedReportIds = [];
    this.usage = [];
    this.favorites = [];
    this.collections = [];
    this.affiliates = [];
    this.bookings = [];
    this.referrals = [];
    this.promoCodes = [];
    this.refreshSignalCount = 0;
    this.persist();
    this.emitChange();
  }

  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emitChange(): void {
    for (const l of this.listeners) {
      try {
        l();
      } catch { /* isolated */ }
    }
  }

  private afterUpdate(): void {
    this.persist();
    this.emitChange();
  }

  // ── Persistence ─────────────────────────────────────────────────────────────

  private persist(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const state: PersistedState = {
        privacy: this.privacy,
        preferences: this.preferences,
        currentCity: this.currentCity,
        currentCountry: this.currentCountry,
        partners: this.partners,
        reports: this.reports,
        votedReportIds: this.votedReportIds,
        usage: this.usage,
        favorites: this.favorites,
        collections: this.collections,
        affiliates: this.affiliates,
        bookings: this.bookings,
        referrals: this.referrals,
        promoCodes: this.promoCodes,
        refreshSignalCount: this.refreshSignalCount,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      storageManager.push('marketplace', 'state', state);
    } catch {
      /* storage unavailable — keep working in memory */
    }
  }

  /** Called by hydrateMarketplaceFromCloud when a newer cloud copy is available. */
  reloadFromStorage(now = Date.now()): void {
    this.load(now);
    this.emitChange();
  }

  private load(_now: number): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const state = JSON.parse(raw) as Record<string, unknown> | null;
      if (state == null || typeof state !== 'object') return;

      const pv = (state.privacy ?? {}) as Record<string, unknown>;
      this.privacy = {
        personalizedRecommendations: pv.personalizedRecommendations === true,
        marketingOptIn: pv.marketingOptIn === true,
        partnerDataSharing: pv.partnerDataSharing === true,
        shareSavedServices: pv.shareSavedServices === true,
        sharePurchaseHistory: pv.sharePurchaseHistory === true,
      };

      const pref = (state.preferences ?? {}) as Record<string, unknown>;
      this.preferences = {
        monthlyBudget: asNumberOrNull(pref.monthlyBudget),
        budgetCurrency: asString(pref.budgetCurrency),
        languages: asStringArray(pref.languages),
        travelStyle: pref.travelStyle === 'budget' || pref.travelStyle === 'balanced' || pref.travelStyle === 'comfort'
          ? pref.travelStyle : null,
        currentNeeds: (Array.isArray(pref.currentNeeds) ? pref.currentNeeds : [])
          .filter((c): c is MarketplaceCategory => MARKETPLACE_CATEGORIES.includes(c as MarketplaceCategory)),
      };

      this.currentCity = asString(state.currentCity);
      this.currentCountry = asString(state.currentCountry);

      this.partners = (Array.isArray(state.partners) ? state.partners : [])
        .filter((p): p is Record<string, unknown> =>
          p != null && typeof p === 'object' &&
          asString((p as Record<string, unknown>).id) != null &&
          asString((p as Record<string, unknown>).name) != null &&
          asString((p as Record<string, unknown>).description) != null &&
          MARKETPLACE_CATEGORIES.includes((p as { category?: never }).category as never))
        .map((p): MarketplacePartner => {
          const pricing = (p.pricing ?? {}) as Record<string, unknown>;
          return {
            id: p.id as string,
            name: (p.name as string).trim(),
            category: p.category as MarketplaceCategory,
            description: (p.description as string).trim(),
            supportedCountries: asStringArray(p.supportedCountries),
            pricing: {
              amount: asNumberOrNull(pricing.amount),
              currency: asString(pricing.currency),
              period: pricing.period === 'oneTime' || pricing.period === 'monthly' || pricing.period === 'yearly'
                ? pricing.period : null,
              summary: asString(pricing.summary),
            },
            verified: p.verified === true,
            availableLanguages: asStringArray(p.availableLanguages),
            contactMethods: (Array.isArray(p.contactMethods) ? p.contactMethods : [])
              .filter((c: unknown): c is ContactMethod =>
                c != null && typeof c === 'object' &&
                CONTACT_KINDS.includes((c as ContactMethod).kind) &&
                asString((c as ContactMethod).value) != null),
            website: asString(p.website),
            remoteWorkerFriendly: typeof p.remoteWorkerFriendly === 'boolean' ? p.remoteWorkerFriendly : null,
            availabilityNote: asString(p.availabilityNote),
            addedAt: asNumberOrNull(p.addedAt) ?? 0,
          };
        })
        .slice(-MAX_PARTNERS);

      const partnerIds = new Set(this.partners.map((p) => p.id));

      this.reports = (Array.isArray(state.reports) ? state.reports : [])
        .filter((r): r is Record<string, unknown> =>
          r != null && typeof r === 'object' &&
          asString((r as Record<string, unknown>).id) != null &&
          typeof (r as Record<string, unknown>).partnerId === 'string' &&
          partnerIds.has((r as Record<string, unknown>).partnerId as string) &&
          MARKETPLACE_REPORT_KINDS.includes((r as { kind?: never }).kind as never) &&
          asString((r as Record<string, unknown>).text) != null &&
          // Only verified-member submissions survive — hard rule.
          (r as Record<string, unknown>).submittedByVerifiedMember === true)
        .map((r): MarketplaceReport => {
          const votes = asNumberOrNull(r.helpfulVotes);
          return {
            id: r.id as string,
            partnerId: r.partnerId as string,
            kind: r.kind as MarketplaceReportKind,
            text: (r.text as string).trim(),
            rating: clampRating(r.rating),
            submittedByVerifiedMember: true,
            helpfulVotes: votes != null && votes >= 0 ? Math.floor(votes) : 0,
            recordedAt: asNumberOrNull(r.recordedAt) ?? 0,
          };
        })
        .slice(-MAX_REPORTS);

      this.votedReportIds = asStringArray(state.votedReportIds);

      this.usage = (Array.isArray(state.usage) ? state.usage : [])
        .filter((u): u is UsageRecord =>
          u != null && typeof u === 'object' &&
          asString((u as Record<string, unknown>).id) != null &&
          typeof (u as UsageRecord).partnerId === 'string' &&
          partnerIds.has((u as UsageRecord).partnerId) &&
          ['positive', 'neutral', 'negative'].includes((u as UsageRecord).outcome))
        .slice(-MAX_USAGE);

      this.favorites = asStringArray(state.favorites).filter((id) => partnerIds.has(id));

      this.collections = (Array.isArray(state.collections) ? state.collections : [])
        .filter((c): c is Record<string, unknown> =>
          c != null && typeof c === 'object' &&
          asString((c as Record<string, unknown>).id) != null &&
          asString((c as Record<string, unknown>).name) != null)
        .map((c): ServiceCollection => ({
          id: c.id as string,
          name: (c.name as string).trim(),
          partnerIds: asStringArray(c.partnerIds).filter((id) => partnerIds.has(id)),
          createdAt: asNumberOrNull(c.createdAt) ?? 0,
        }))
        .slice(-MAX_COLLECTIONS);

      this.affiliates = (Array.isArray(state.affiliates) ? state.affiliates : [])
        .filter((a): a is AffiliatePartnershipRef =>
          a != null && typeof a === 'object' &&
          asString((a as Record<string, unknown>).id) != null &&
          typeof (a as AffiliatePartnershipRef).partnerId === 'string' &&
          partnerIds.has((a as AffiliatePartnershipRef).partnerId) &&
          asString((a as Record<string, unknown>).program) != null &&
          // Disclosure is mandatory — undisclosed affiliates are dropped.
          asString((a as Record<string, unknown>).disclosure) != null)
        .slice(-MAX_COMMERCE_REFS);

      this.bookings = (Array.isArray(state.bookings) ? state.bookings : [])
        .filter((b): b is BookingRequestRef =>
          b != null && typeof b === 'object' &&
          asString((b as Record<string, unknown>).id) != null &&
          typeof (b as BookingRequestRef).partnerId === 'string' &&
          partnerIds.has((b as BookingRequestRef).partnerId) &&
          BOOKING_STATUSES.includes((b as BookingRequestRef).status))
        .slice(-MAX_COMMERCE_REFS);

      this.referrals = (Array.isArray(state.referrals) ? state.referrals : [])
        .filter((r): r is ReferralRef =>
          r != null && typeof r === 'object' &&
          asString((r as Record<string, unknown>).id) != null &&
          typeof (r as ReferralRef).partnerId === 'string' &&
          partnerIds.has((r as ReferralRef).partnerId) &&
          asString((r as Record<string, unknown>).code) != null)
        .slice(-MAX_COMMERCE_REFS);

      this.promoCodes = (Array.isArray(state.promoCodes) ? state.promoCodes : [])
        .filter((p): p is PromoCodeRef =>
          p != null && typeof p === 'object' &&
          asString((p as Record<string, unknown>).id) != null &&
          typeof (p as PromoCodeRef).partnerId === 'string' &&
          partnerIds.has((p as PromoCodeRef).partnerId) &&
          asString((p as Record<string, unknown>).code) != null)
        .slice(-MAX_COMMERCE_REFS);

      const refresh = asNumberOrNull(state.refreshSignalCount);
      this.refreshSignalCount = refresh != null && refresh >= 0 ? Math.floor(refresh) : 0;
    } catch {
      /* corrupt state — start fresh rather than crash */
    }
  }
}

/** App-wide singleton. Call attachToCore() during app bootstrap (after roamerCore.initialize()). */
export const roamerMarketplaceModule = new RoamerMarketplaceModule();

/**
 * Boot hydration: fetches the cloud copy of the Marketplace™ state if it is
 * strictly newer than the last local write, writes it to localStorage, and
 * signals the module to reload.  Fire-and-forget — boot never blocks the network.
 */
export async function hydrateMarketplaceFromCloud(): Promise<boolean> {
  const cloud = await storageManager.hydrate<unknown>('marketplace', 'state');
  if (!cloud) return false;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('nomad:marketplace:v1', JSON.stringify(cloud));
    }
  } catch { return false; }
  roamerMarketplaceModule.reloadFromStorage();
  return true;
}
